import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ChevronRight, Check } from 'lucide-react';
import FormBuilder, { FormField } from '@/components/company/FormBuilder';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { useCompanyProfile, useUpdateCompanyField } from '@/hooks/useCompanyProfile';
import { PurposeSkeleton } from '@/components/company/skeletons';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import StepTipsPanel from '@/components/company/StepTipsPanel';
import { useAutosave } from '@/hooks/useAutosave';
import DraftBanner from '@/components/form/DraftBanner';
import AutosaveIndicator from '@/components/form/AutosaveIndicator';

interface PurposeFormValues {
  purpose: string;
  is_published_purpose: boolean;
}

export default function Purpose() {
  const { currentTenant } = useTenantState();
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const isReadOnly = useIsReadOnly();
  const { companyProfile } = useCompanyState();
  const { isLoading } = useCompanyProfile(currentTenant?.id);
  const updateFieldMutation = useUpdateCompanyField();

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const purposeStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'purpose');

  const videoLink = purposeStep?.video_link;
  const tips = purposeStep?.tips;

  const formControl = useForm<PurposeFormValues>({
    defaultValues: {
      purpose: '',
      is_published_purpose: false,
    },
  });

  const { handleSubmit, reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const [fields, setFields] = useState<FormField[]>([]);
  const [showPanel, setShowPanel] = useState(true);
  // Gates autosave until the form holds server data, so the empty defaults
  // present before reset() are never written over a real draft.
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const autosave = useAutosave({
    form: formControl,
    formKey: 'purpose',
    enabled: isDataLoaded && !isReadOnly,
    // Server-owned publish flag; the page reloads it and a stale draft value
    // must not flip it back.
    exclude: ['is_published_purpose'],
  });

  useEffect(() => {
    if (!companyProfile) {
      setIsDataLoaded(true);
      return;
    }

    const purposeObj =
      typeof companyProfile.purpose === 'object' && companyProfile.purpose !== null
        ? (companyProfile.purpose as { value: string; is_published: boolean })
        : null;

    reset({
      purpose: purposeObj ? purposeObj.value : (companyProfile.purpose as string) ?? '',
      is_published_purpose: purposeObj ? purposeObj.is_published : Boolean(companyProfile.is_published_purpose),
    });
    setIsDataLoaded(true);
  }, [companyProfile, reset]);

  const handleSave = async (data: PurposeFormValues) => {
    if (!currentTenant?.id) return;

    // Offline: the request would fail and lose the edits, so hold them locally
    // and let the user retry once the connection is back.
    if (!autosave.isOnline) {
      autosave.flush({ pendingSync: true });
      toast.info('Anda sedang offline. Perubahan disimpan sebagai draft di perangkat ini.');
      return;
    }

    await updateFieldMutation.mutateAsync({
      tenantId: currentTenant.id,
      field: 'purpose',
      value: data.purpose,
      isPublished: data.is_published_purpose,
    });
    // Server now holds these values; the draft would only be noise on return.
    autosave.clearDraft();
    toast.success('Tujuan berhasil disimpan');
    navigate(`/${tenant_id}/positioning`);
  };

  useEffect(() => {
    setFields([
      {
        name: 'purpose',
        label: 'Tujuan Perusahaan',
        type: 'markdown',
        required: true,
        placeholder: 'Tujuan perusahaan Anda...',
      },
    ]);
  }, []);

  if (isLoading) {
    return <PurposeSkeleton />;
  }

  const isSaving = updateFieldMutation.isLoading;

  return (
    <section className="w-full">
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Purpose</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Anda akan menyusun motivasi abadi membangun bisnis (Durasi 100 tahun).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isReadOnly && <AutosaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />}

          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6">
            {autosave.pendingDraft && (
              <DraftBanner
                savedAt={autosave.pendingDraft.savedAt}
                pendingSync={autosave.pendingDraft.pendingSync}
                onRestore={autosave.restoreDraft}
                onDiscard={autosave.discardDraft}
              />
            )}

            <FormProvider {...formControl}>
              <form ref={formRef} onSubmit={handleSubmit(handleSave)}>
                <FormBuilder fields={fields} hidePublish disabled={isReadOnly} dark />
              </form>
            </FormProvider>
          </div>
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0 min-w-0">
            <StepTipsPanel videoLink={videoLink} tips={tips} />
          </div>
        )}
      </div>
    </section>
  );
}
